import { useEffect, useMemo, useState } from "react";
import { streamIntervalMs, tokenizeForStream } from "../hooks/streamText";

type SmoothStreamTextProps = {
  text: string;
  className?: string;
  asCode?: boolean;
  animate?: boolean;
};

export default function SmoothStreamText({
  text,
  className,
  asCode = false,
  animate = true,
}: SmoothStreamTextProps) {
  const tokens = useMemo(
    () => (asCode ? [{ type: "code" as const, value: text }] : tokenizeForStream(text)),
    [asCode, text],
  );
  const [visibleCount, setVisibleCount] = useState(() =>
    animate ? 0 : tokens.length,
  );

  useEffect(() => {
    if (!animate) {
      setVisibleCount(tokens.length);
      return;
    }

    setVisibleCount(0);
    let cancelled = false;
    let timer = 0;
    let index = 0;

    const tick = () => {
      if (cancelled) return;
      index += 1;
      setVisibleCount(index);
      if (index >= tokens.length) return;
      timer = window.setTimeout(tick, streamIntervalMs(tokens[index]));
    };

    const firstDelay = tokens[0] ? streamIntervalMs(tokens[0]) : 0;
    timer = window.setTimeout(tick, firstDelay);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [animate, tokens]);

  const visible = tokens.slice(0, visibleCount);
  const label = asCode ? text : text.replace(/`/g, "");

  return (
    <span aria-label={label} className={className}>
      {visible.map((token, index) => {
        const isFrontier = animate && index === visibleCount - 1;
        const tokenClass = isFrontier ? "inbox-demo-stream-token" : undefined;

        if (token.type === "code" || asCode) {
          return (
            <code
              className={
                tokenClass
                  ? "inbox-demo-inline-code inbox-demo-stream-token"
                  : "inbox-demo-inline-code"
              }
              key={`code-${index}`}
            >
              {token.value}
            </code>
          );
        }

        return (
          <span className={tokenClass} key={`text-${index}`}>
            {token.value === " " ? "\u00A0" : token.value}
          </span>
        );
      })}
    </span>
  );
}
